import { RESET, getChallenge } from './exercises';
import quotes from './quotes.json';
import type { Path } from './types';

/**
 * The crew (well/product/12-the-crew-pass.md, crew/crew.json): four parts of a
 * creative mind, the same faces for everyone, working only from the person's
 * own material. Kraben speaks wherever there is no exercise; the day's guide
 * owns the deal, runner and kept screens; The Moon signs what the crew find in your work.
 * Copy here is placeholder until the crew's writing pass lands.
 */
export type CrewId = 'kraben' | 'mrs-jennys' | 'the-moon' | 'the-censor';

export interface CrewMember {
  id: CrewId;
  name: string;
  part: string;
  colour: string;
  /** one-letter mark for the avatar until the cutouts land */
  mark: string;
}

export const CREW: Record<CrewId, CrewMember> = {
  kraben: { id: 'kraben', name: 'Kraben', part: 'Showing up', colour: '#F2705B', mark: 'K' },
  'mrs-jennys': { id: 'mrs-jennys', name: 'Mrs. Jennys', part: 'Permission to be bad', colour: '#FFC53D', mark: 'J' },
  'the-moon': { id: 'the-moon', name: 'The Moon', part: 'Ideas at night', colour: '#6179A8', mark: '☾' },
  'the-censor': { id: 'the-censor', name: 'The Censor', part: 'The voice that says no', colour: '#1B4FA8', mark: 'C' },
};

/** Draft mapping from crew.json dayGuides, keyed by exercise. */
export const DAY_GUIDES: Record<string, CrewId> = {
  'ten-bad-ideas': 'mrs-jennys', 'one-thing-ten-ways': 'the-moon', 'fortunately-unfortunately': 'the-moon', 'make-something-ugly': 'mrs-jennys',
  'six-word-film': 'kraben', 'emoji-screenplay': 'the-moon', 'rename-everything': 'kraben', 'worst-movie-pitch': 'mrs-jennys',
  'wrong-caption': 'the-moon', 'portmanteau-product': 'the-moon', 'two-truths-and-a-film': 'kraben', 'blind-contour': 'mrs-jennys',
  'fake-trailer': 'mrs-jennys', 'complaint-letter': 'the-censor',
};

export function guideFor(challengeId: string): CrewMember {
  return CREW[DAY_GUIDES[challengeId] || 'kraben'];
}

export type Anchor = 'coffee' | 'commute' | 'lunch' | 'evening' | 'time';
export const ANCHORS: { id: Anchor; label: string; hour: number }[] = [
  { id: 'coffee', label: 'With my first coffee', hour: 7 },
  { id: 'commute', label: 'On the way in', hour: 8 },
  { id: 'lunch', label: 'Lunch break', hour: 12 },
  { id: 'evening', label: 'After the day is done', hour: 21 },
  { id: 'time', label: 'Pick a time', hour: 9 },
];

/** Kraben's greeting on Home, anchored to the person's moment (H-01). Never a day count, never a gap. */
export function greeting(anchor: Anchor | undefined, name: string | undefined, now = new Date()): { moment: string; line: string } {
  const day = now.toLocaleDateString(undefined, { weekday: 'long' });
  const h = now.getHours();
  const tod = h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening';
  const who = name ? `, ${name}` : '';
  const moment = anchor === 'coffee' ? `First coffee · ${day}` : anchor === 'commute' ? `On the way in · ${day}` : anchor === 'lunch' ? `Lunch · ${day}` : anchor === 'evening' ? `Day done · ${day}` : day;
  return { moment, line: `${tod}${who}.` };
}

export interface CrewPost {
  id: string;
  by: CrewId;
  /** the count-up day it appears on, 0 = the first look at the feed */
  day: number;
  kind: 'intro' | 'note' | 'drawing' | 'quote';
  text: string;
  /** position within the day, so a same-day intro always comes first */
  order: number;
  /** a quote's attribution, when it is one */
  from?: string;
  /** the exercise a note deals, so the feed can show its picture */
  exercise?: string;
}

/** Mrs. Jennys draws badly and posts it anyway; these are her captions, one every few days. */
const JENNYS_DRAWINGS = [
  'Drew a cat. It is a terrible cat. Posting it anyway!',
  'Tried to draw a swan. Got a goose with ambitions. Keeping it.',
  'lol. A horse, from memory. The memory was not of a horse.',
  'My kettle. It looks alarmed. Eep!',
  'Drew the view from the bus. The bus was moving, I did my best.',
  'A self-portrait, three lines. Two of them are hair.',
  'Attempted a bicycle. Bicycles are a lie; nobody can draw one.',
  'The dog next door. He sat for eleven seconds. Eleven seconds of dog.',
  'A lighthouse. Or a very confident pencil. Still can’t tell.',
  'Drew my hand without looking. It has the right number of fingers, roughly.',
];

/** The Censor posts rarely, and someone always answers her (12 §7 recommendation). */
const CENSOR_DAYS: Record<number, [string, CrewId, string]> = {
  6: ['I looked at the emoji one. I have notes. Nobody asked.', 'mrs-jennys', 'She always has notes. Draw the next one anyway.'],
  13: ['Sixty words of trailer voiceover. Out loud. I will be listening, obviously.', 'kraben', 'She listens to everything. Say it anyway; that is the exercise.'],
  20: ['Twenty days and not one of these is finished properly.', 'the-moon', 'Finished properly is not a thing. Finished is.'],
  27: ['I would like it noted that the complaint letter was, in fact, good.', 'kraben', 'Noted. She won’t say it twice.'],
};

/** A special line on a few days, in Kraben’s voice, keyed to the reset. */
const KRABEN_SPECIALS: Record<number, string> = {
  7: 'Seven days of making things. That is a week. The room you are in is today’s material.',
  14: 'Two weeks. The Censor has the exercise today. She is good at complaints; today it is pointed at your left shoe.',
  21: 'Three weeks. Look back at Day 1 on your feed sometime. Not now. Sometime.',
  30: 'Thirty days of making things. Everything in your film is already here.',
};

/**
 * Nobody posts on your feed before they have introduced themselves and you have
 * said hello back (owner's rule, 2026-09-17/18: that includes Kraben). The day
 * here is the count-up day their knock appears at the top of the feed; the
 * intro post is their first post and it lands the day you let them in. The crew
 * turn up one at a time, a few exercises apart, so the feed starts as yours
 * alone. crew.test.ts holds the line.
 */
export const INTROS: Record<CrewId, { day: number; text: string }> = {
  kraben: { day: 2, text: 'I’m Kraben. I deal you one small thing a day and I hold the clock. That is the whole job. This is your feed: nobody sees it unless you send them something. From now on I’ll post here when the next one is dealt.' },
  'mrs-jennys': { day: 3, text: 'Mrs. Jennys. Thirty years of teaching English, and I draw badly on purpose. Permission to be bad is my department. I post mine so you post yours.' },
  'the-moon': { day: 5, text: 'The Moon. I am up at 3 a.m. anyway, so I read what you made and post what I find. Mostly other people’s words. Sometimes a thought.' },
  'the-censor': { day: 9, text: 'The Censor. I am the voice that says no. I am on your crew so you can hear me coming, and so someone can answer me.' },
};

type Draft = Omit<CrewPost, 'order'>;

function buildScript(): CrewPost[] {
  const posts: Draft[] = [];
  const seq = RESET.writing;
  let qi = 0, ji = 0;
  const here = (id: CrewId, day: number) => day >= INTROS[id].day;
  for (let day = 1; day <= 30; day++) {
    for (const id of Object.keys(INTROS) as CrewId[]) if (INTROS[id].day === day) posts.push({ id: `${id}-intro`, by: id, day, kind: 'intro', text: INTROS[id].text });
    // Kraben deals the next day the night before, once he is here
    if (!here('kraben', day)) { /* not yet */ }
    else if (KRABEN_SPECIALS[day]) posts.push({ id: `k-${day}`, by: 'kraben', day, kind: 'note', text: KRABEN_SPECIALS[day] });
    else if (day < 14) {
      const next = getChallenge(seq[day])!;
      posts.push({ id: `k-${day}`, by: 'kraben', day, kind: 'note', text: `Day ${day + 1} is dealt: ${next.title}. ${next.encouragement || ''}`.trim(), exercise: next.id });
    }
    // The Moon, every other night: a quote found at 3 a.m., or a thought
    if (!here('the-moon', day)) { /* not yet */ }
    else if (day % 2 === 1) {
      const q = quotes.quotes[qi++ % quotes.quotes.length];
      posts.push({ id: `m-${day}`, by: 'the-moon', day, kind: 'quote', text: q.text, from: q.author });
    } else if (day % 6 === 0) {
      posts.push({ id: `m-${day}`, by: 'the-moon', day, kind: 'note', text: '3 a.m. thought: every bad idea is a good idea that arrived early.' });
    }
    // Mrs. Jennys, every third day: a terrible drawing
    if (here('mrs-jennys', day) && day % 3 === 0) posts.push({ id: `j-${day}`, by: 'mrs-jennys', day, kind: 'drawing', text: JENNYS_DRAWINGS[ji++ % JENNYS_DRAWINGS.length] });
    // The Censor, rarely, always answered
    const c = CENSOR_DAYS[day];
    if (c && here('the-censor', day) && here(c[1], day)) { posts.push({ id: `c-${day}`, by: 'the-censor', day, kind: 'note', text: c[0] }); posts.push({ id: `c-${day}-a`, by: c[1], day, kind: 'note', text: c[2] }); }
  }
  // order within a day = list order, with every intro moved to the front of its day
  const byDay = new Map<number, Draft[]>();
  for (const p of posts) byDay.set(p.day, [...(byDay.get(p.day) || []), p]);
  const out: CrewPost[] = [];
  for (const [, list] of [...byDay.entries()].sort((a, b) => a[0] - b[0])) {
    const sorted = [...list.filter((p) => p.kind === 'intro'), ...list.filter((p) => p.kind !== 'intro')];
    sorted.forEach((p, i) => out.push({ ...p, order: i }));
  }
  return out;
}

/** The crew whose knock has come by this count-up day: the only ones who may ask to be let onto the feed. */
export function crewSeenBy(madeDays: number): CrewId[] {
  return (Object.keys(INTROS) as CrewId[]).filter((id) => INTROS[id].day <= madeDays);
}

/** The next crew member waiting at the door of the feed: their day has come and hello has not been said back. */
export function pendingMeet(madeDays: number, met: Record<string, number> | undefined): CrewId | null {
  return crewSeenBy(madeDays).find((id) => !(met && id in met)) ?? null;
}

/** The crew who are actually on the feed: knocked, and let in. */
export function crewMet(met: Record<string, number> | undefined): CrewId[] {
  return (Object.keys(CREW) as CrewId[]).filter((id) => !!met && id in met);
}

/**
 * The crew's script: authored specials plus a generated thirty days, the same
 * for everyone, keyed to the person's count-up day. The crew never reacts to
 * the person's posts. Placeholder voice until the writing pass (12 §7).
 */
export const CREW_SCRIPT: CrewPost[] = buildScript();

/** What the day's guide says on the deal and the kept screen; one voice each, until the per-day lines are written. */
export const GUIDE_LINES: Record<CrewId, { deal: string; kept: string }> = {
  kraben: { deal: 'I hold the time. You do the rest.', kept: 'Kept. That is one more than yesterday.' },
  'mrs-jennys': { deal: 'Make it badly. I insist.', kept: 'Terrible. Wonderful. Pinning it up.' },
  'the-moon': { deal: 'Go fast. The strange ones are mine.', kept: 'I will look at this again tonight.' },
  'the-censor': { deal: 'Formal register. I will be checking the grammar.', kept: 'Adequate. Which, from me, is a lot.' },
};

export function crewPostsUpTo(day: number): CrewPost[] {
  return CREW_SCRIPT.filter((p) => p.day <= day);
}

/**
 * The posts that may show: by crew who have been let in, up to today. The
 * intro lands on the day hello was said back (which may be later than the
 * knock), and nothing of theirs from before that day is shown.
 */
export function crewPostsFor(madeDays: number, met: Record<string, number> | undefined): CrewPost[] {
  if (!met) return [];
  const out: CrewPost[] = [];
  for (const p of CREW_SCRIPT) {
    if (!(p.by in met)) continue;
    const since = Math.max(met[p.by], INTROS[p.by].day);
    if (p.kind === 'intro') { if (since <= madeDays) out.push({ ...p, day: since }); continue; }
    if (p.day >= since && p.day <= madeDays) out.push(p);
  }
  return out;
}

/** Tomorrow's guide, for the small face next to tomorrow's exercise on Home (12 §3 recommendation). */
export function guideForDay(day: number, path: Path): CrewMember {
  const seq = RESET[path];
  return guideFor(seq[(Math.max(1, day) - 1) % seq.length]);
}
